import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { Trash2 } from 'lucide-react';
import BotoneraModern from '../../components/Botonera/BotoneraModern.tsx';
import useNavVisibility from '../../hooks/useNavVisibility.ts';
import { useActionGuard } from '../../hooks/useActionGuard.ts';
import Breadcrumbs from '../../components/UI/Breadcrumbs/Breadcrumbs.tsx';
import CommerceForm from '../../components/OnboardingV2/ConversationalOnboarding/CommerceOnboarding/CommerceForm.tsx';
import type { CommerceFormSubmitPayload } from '../../components/OnboardingV2/ConversationalOnboarding/CommerceOnboarding/CommerceFormTypes.ts';
import {
  deleteCommerce,
  getCommerce,
  getCommerceJurisdictionDepartments,
  getCommerceListedZones,
  parseCommerceDetailForEdit,
  patchCommerce,
  type CommerceFormEditSeed,
  type ListedZonesByJurisdictionBucketDto,
} from '../../services/commerceService.ts';
import { loadUserData } from '../../store/actions';
import { closeMainModal, openMainModal } from '../../store/actions/profileActions';
import { normalizeUserCommerces } from '../../utils/userCommerces.ts';
import type { CommerceFormPrefill } from '../../utils/commercePrefill.ts';
import { buildNavigationParams, getUserDisplayName } from '../../utils/navigationHelpers.ts';
import styles from './CommerceAltaPage.module.scss';

async function fetchCommerceEditParsed(commerceId: number): Promise<{
  parsed: NonNullable<ReturnType<typeof parseCommerceDetailForEdit>>;
  inner: Record<string, unknown> | undefined;
} | null> {
  const detailRes = await getCommerce(commerceId);
  const raw = detailRes.data as Record<string, unknown> | undefined;
  const inner = (raw?.data ?? raw) as Record<string, unknown> | undefined;
  const scope =
    inner?.deliveryScope ??
    inner?.delivery_scope ??
    (inner?.deliveryGeo as Record<string, unknown> | undefined)?.deliveryScope ??
    (inner?.delivery_geo as Record<string, unknown> | undefined)?.delivery_scope;
  let listedRes: { data?: unknown } | null = null;
  if (scope === 'LISTED_ZONES') {
    try {
      listedRes = await getCommerceListedZones(commerceId);
    } catch {
      listedRes = null;
    }
  }
  const parsed = parseCommerceDetailForEdit(detailRes, listedRes);
  if (!parsed) return null;
  return { parsed, inner };
}

const CommerceEdicionPage: React.FC = () => {
  const dispatch = useDispatch<any>();
  const navigate = useNavigate();
  const { checkAccess } = useActionGuard();
  const location = useLocation();
  const { commerceId: commerceIdParam } = useParams<{ commerceId: string }>();
  const { ocultarEnMobile, mobileStylesFromParent } = useNavVisibility();
  const userData = useSelector((s: { general?: { userData?: Record<string, unknown> } }) => s.general?.userData);
  const [busy, setBusy] = useState(false);
  const [deletingCommerce, setDeletingCommerce] = useState(false);
  const [loadError, setLoadError] = useState('');
  const [prefill, setPrefill] = useState<CommerceFormPrefill | null>(null);
  const [editSeed, setEditSeed] = useState<CommerceFormEditSeed | null>(null);
  const [formBaselineKey, setFormBaselineKey] = useState(0);

  const userId = userData?.id != null ? Number(userData.id) : 0;
  const commerces = useMemo(() => normalizeUserCommerces(userData), [userData]);
  const isAdmin = userData?.type === 'ADMIN';
  const commerceId = useMemo(() => {
    const n = commerceIdParam != null ? Number(commerceIdParam) : NaN;
    return Number.isFinite(n) ? n : NaN;
  }, [commerceIdParam]);
  const profileNavQuery = useMemo(() => {
    const source = new URLSearchParams(location.search);
    const out = new URLSearchParams();
    const keys = ['from', 'tab', 'section', 'userId', 'userName'];
    for (const key of keys) {
      const value = source.get(key);
      if (value) out.set(key, value);
    }
    return out.toString();
  }, [location.search]);
  const listUrl = useMemo(() => {
    const out = new URLSearchParams();
    if (Number.isFinite(commerceId)) out.set('commerceId', String(commerceId));
    if (profileNavQuery) {
      const nav = new URLSearchParams(profileNavQuery);
      nav.forEach((value, key) => out.set(key, value));
    }
    const query = out.toString();
    return query ? `/comercios/lista-precios?${query}` : '/comercios/lista-precios';
  }, [commerceId, profileNavQuery]);

  const gestionComercioUrl = useMemo(() => {
    const displayName = getUserDisplayName(userData) ?? 'Perfil';
    return `/perfil/${userId}${buildNavigationParams('Gestión', String(userId), displayName, 'comercio')}`;
  }, [userData, userId]);

  const ownsCommerce = useMemo(
    () => commerces.some((c) => c.id === commerceId),
    [commerceId, commerces]
  );

  useEffect(() => {
    if (!userId) return;
    if (!Number.isFinite(commerceId)) {
      navigate(listUrl, { replace: true });
      return;
    }
    if (!isAdmin && !ownsCommerce) {
      navigate(listUrl, { replace: true });
      return;
    }
    let cancelled = false;
    setLoadError('');
    setPrefill(null);
    setEditSeed(null);
    void (async () => {
      try {
        const snap = await fetchCommerceEditParsed(commerceId);
        if (cancelled) return;
        if (!snap) {
          setLoadError('No pudimos leer los datos del comercio.');
          return;
        }
        const { parsed, inner } = snap;
        if (inner && typeof inner.user === 'object' && inner.user != null && !isAdmin) {
          const u = inner.user as Record<string, unknown>;
          const ownerId = Number(u.id);
          if (Number.isFinite(ownerId) && ownerId !== userId) {
            setLoadError('No tenés permiso para editar este comercio.');
            return;
          }
        }
        setPrefill(parsed.prefill);
        setEditSeed(parsed.editSeed);
      } catch (e: unknown) {
        const msg =
          (e as { response?: { data?: { message?: string } } })?.response?.data?.message ||
          'No pudimos cargar el comercio.';
        if (!cancelled) setLoadError(msg);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [commerceId, isAdmin, listUrl, navigate, ownsCommerce, userId]);

  const handleSkip = useCallback(() => {
    navigate(gestionComercioUrl);
  }, [gestionComercioUrl, navigate]);

  const runDeleteCommerce = useCallback(async () => {
    if (!Number.isFinite(commerceId) || deletingCommerce) return;
    setDeletingCommerce(true);
    dispatch(
      openMainModal({
        isLoading: true,
        title: 'Eliminando comercio',
        body: 'Estamos eliminando el comercio y su catálogo de materiales…',
        isSuccess: true,
        noActionNeeded: true,
      })
    );
    try {
      await deleteCommerce(commerceId);
      dispatch(loadUserData());
      dispatch(
        openMainModal({
          title: 'Comercio eliminado',
          body: 'El comercio y su catálogo de materiales se eliminaron correctamente.',
          iconType: 'success',
          isSuccess: true,
          noLabel: 'Volver',
          confirmHandler: null,
          customCloseMainModal: () => {
            dispatch(closeMainModal());
            if (!checkAccess('register_commerce')) {
              navigate('/dashboard', { replace: true });
              return;
            }
            navigate('/comercios/alta', { replace: true });
          },
        })
      );
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ||
        'No pudimos eliminar el comercio. Intentá de nuevo en unos minutos.';
      dispatch(
        openMainModal({
          title: 'No pudimos eliminar el comercio',
          body: msg,
          iconType: 'error',
          isSuccess: true,
          confirmHandler: null,
          noLabel: 'Cerrar',
        })
      );
    } finally {
      setDeletingCommerce(false);
    }
  }, [checkAccess, commerceId, deletingCommerce, dispatch, navigate]);

  const handleDeleteCommerce = useCallback(() => {
    if (!Number.isFinite(commerceId) || deletingCommerce) return;
    dispatch(
      openMainModal({
        title: 'Eliminar comercio',
        body:
          'Esta acción elimina tu comercio y todo su catálogo de materiales. No se puede deshacer.',
        iconType: 'warning',
        isSuccess: false,
        yesLabel: 'Eliminar comercio',
        noLabel: 'Cancelar',
        confirmHandler: () => {
          void runDeleteCommerce();
        },
        customCloseMainModal: () => {
          dispatch(closeMainModal());
        },
      })
    );
  }, [commerceId, deletingCommerce, dispatch, runDeleteCommerce]);

  const handleSubmit = useCallback(
    async (payload: CommerceFormSubmitPayload) => {
      setBusy(true);
      dispatch(
        openMainModal({
          isLoading: true,
          title: 'Guardando cambios',
          body: 'Estamos guardando los datos del comercio…',
          isSuccess: true,
          noActionNeeded: true,
        })
      );
      try {
        let listedZonesByJurisdictionValidated: ListedZonesByJurisdictionBucketDto[] | undefined;
        let listedGeorefZonaIds: string[] | undefined =
          payload.deliveryScope === 'LISTED_ZONES' ? payload.listedGeorefZonaIds : undefined;
        if (
          payload.deliveryScope === 'LISTED_ZONES' &&
          payload.listedZonesByJurisdiction &&
          payload.listedZonesByJurisdiction.length > 0
        ) {
          const validated: ListedZonesByJurisdictionBucketDto[] = [];
          try {
            for (const bucket of payload.listedZonesByJurisdiction) {
              const depRes = await getCommerceJurisdictionDepartments(bucket.jurisdiccionId);
              const cities = depRes.data?.data?.cities;
              const allowed = new Set(Array.isArray(cities) ? cities.map((c) => c.id) : []);
              const filtered = bucket.georefZonaIds.filter((id) => allowed.has(id));
              if (filtered.length !== bucket.georefZonaIds.length) {
                dispatch(
                  openMainModal({
                    title: 'Zonas y provincia',
                    body:
                      filtered.length === 0
                        ? 'Ninguna de las zonas de una de las provincias elegidas está en el catálogo de esa provincia. Revisá la selección.'
                        : 'Hay zonas que no pertenecen al catálogo de su provincia. Quitá las que no correspondan y guardá de nuevo.',
                    iconType: 'error',
                    isSuccess: true,
                    confirmHandler: null,
                  })
                );
                return;
              }
              validated.push({ jurisdiccionId: bucket.jurisdiccionId, georefZonaIds: filtered });
            }
            listedZonesByJurisdictionValidated = validated;
          } catch {
            listedZonesByJurisdictionValidated = payload.listedZonesByJurisdiction;
          }
        } else if (
          payload.deliveryScope === 'LISTED_ZONES' &&
          listedGeorefZonaIds &&
          listedGeorefZonaIds.length > 0
        ) {
          try {
            const depRes = await getCommerceJurisdictionDepartments(payload.jurisdiccionId);
            const cities = depRes.data?.data?.cities;
            const allowed = new Set(Array.isArray(cities) ? cities.map((c) => c.id) : []);
            const filtered = listedGeorefZonaIds.filter((id) => allowed.has(id));
            if (filtered.length !== listedGeorefZonaIds.length) {
              dispatch(
                openMainModal({
                  title: 'Zonas y provincia',
                  body:
                    filtered.length === 0
                      ? 'Ninguna de las zonas elegidas está en el catálogo de la provincia seleccionada. Cambiá la provincia o elegí zonas solo del listado de esa provincia.'
                      : 'Hay zonas que no pertenecen al catálogo de la provincia seleccionada. Quitá las que no correspondan y guardá de nuevo.',
                  iconType: 'error',
                  isSuccess: true,
                  confirmHandler: null,
                })
              );
              return;
            }
            listedGeorefZonaIds = filtered;
          } catch {
            /* el servidor valida si falla el catálogo */
          }
        }
        await patchCommerce(commerceId, {
          name: payload.name,
          description: payload.description,
          zone: payload.zone,
          delivery: payload.delivery,
          delivery_condition: payload.delivery_condition,
          url_site_web: payload.url_site_web,
          jurisdiccionId: payload.jurisdiccionId,
          userId: payload.userId,
          logo: payload.logo,
          deliveryScope: payload.deliveryScope,
          deliveryRadiusKm:
            payload.deliveryScope === 'LOCAL' ? payload.deliveryRadiusKm : undefined,
          latitude: payload.deliveryScope === 'LOCAL' ? payload.latitude : undefined,
          longitude: payload.deliveryScope === 'LOCAL' ? payload.longitude : undefined,
          listedZonesByJurisdiction: listedZonesByJurisdictionValidated,
          listedGeorefZonaIds:
            listedZonesByJurisdictionValidated != null && listedZonesByJurisdictionValidated.length > 0
              ? undefined
              : listedGeorefZonaIds,
        });
        const refreshed = await fetchCommerceEditParsed(commerceId);
        if (refreshed) {
          setPrefill(refreshed.parsed.prefill);
          setEditSeed(refreshed.parsed.editSeed);
          setFormBaselineKey((k) => k + 1);
        }
        dispatch(loadUserData());
        dispatch(
          openMainModal({
            title: 'Datos actualizados',
            body: 'Los cambios del comercio quedaron guardados.',
            iconType: 'success',
            isSuccess: true,
            confirmHandler: () => {
              navigate(gestionComercioUrl);
            },
          })
        );
      } catch (err: unknown) {
        const msg =
          (err as { response?: { data?: { message?: string } } })?.response?.data?.message ||
          'No pudimos guardar los cambios. Intentá de nuevo.';
        dispatch(
          openMainModal({
            title: 'Editar comercio',
            body: msg,
            iconType: 'error',
            isSuccess: true,
            confirmHandler: null,
          })
        );
      } finally {
        setBusy(false);
      }
    },
    [commerceId, dispatch, gestionComercioUrl, navigate]
  );

  if (!userId) {
    return null;
  }

  return (
    <div className={styles.container}>
      {ocultarEnMobile && <BotoneraModern mobileStylesFromParent={mobileStylesFromParent} />}
      <div className={styles.wrapper}>
        <div className={styles.breadcrumbBar}>
          <Breadcrumbs />
        </div>
        <main className={styles.main}>
          {loadError ? (
            <div className={styles.commerceStack}>
              <p className={styles.subtitle}>{loadError}</p>
              <button type="button" className={styles.pickPrimaryBtn} onClick={handleSkip}>
                Volver al listado de precios
              </button>
            </div>
          ) : prefill == null || editSeed == null ? (
            <div className={styles.commerceStack}>
              <p className={styles.subtitle}>Cargando datos del comercio…</p>
            </div>
          ) : (
            <div className={styles.commerceStack}>
              <div className={styles.commerceHero}>
                <div className={styles.commerceHeroIntro}>
                  <div className={styles.commerceHeroHeadRow}>
                    <h1 className={styles.commerceHeroTitle}>Editar comercio</h1>
                    <button
                      type="button"
                      className={styles.commerceDeleteHeaderButton}
                      disabled={busy || deletingCommerce}
                      onClick={handleDeleteCommerce}
                      aria-label="Eliminar comercio"
                      title="Eliminar comercio"
                    >
                      <Trash2 size={16} aria-hidden />
                      <span>{deletingCommerce ? 'Eliminando…' : 'Eliminar comercio'}</span>
                    </button>
                  </div>
                  <div className={styles.sheetIntro}>
                    <p className={styles.subtitle}>
                      Actualizá nombre, descripción, ubicación, alcance de entrega, envíos y logo. El listado de precios
                      se gestiona aparte en la pantalla de planilla.
                    </p>
                  </div>
                </div>
              </div>
              <div className={styles.commerceMain}>
                <CommerceForm
                  key={`${commerceId}-${formBaselineKey}`}
                  prefill={prefill}
                  userId={userId}
                  disabled={busy}
                  cancelLabel="Volver"
                  mode="edit"
                  editSeed={editSeed}
                  flexibleSheetEnabled={false}
                  onSkip={handleSkip}
                  onSubmit={handleSubmit}
                />
              </div>
            </div>
          )}
        </main>
      </div>
    </div>
  );
};

export default CommerceEdicionPage;
